import Axios from "axios";
import store from "@/store";
import storage from "@/services/storage";

import responseStatus from "@/services/responseStatus";

import config from "@/config";
import {
  startLoading,
  endLoading,
  showLoading,
} from "@/store/slices/loading/loadingSlice";
import { toast } from "react-toastify";
import { toastMsg } from "@/services/variables";
import { checkAuthToken, getCookie } from "@/services/generic.methods";
import { globalLogout } from "@/services/generic-logout-method";
import { RESETPASSWORD } from "./endpoints";

/**
 * @name @ApiService
 * @description Default Api Service for Auth and Defaul Queries
 * */

/* General ApiServices requests and configurations */

/** Creating Axios instance */
const instance = Axios.create({
  baseURL: config.webApiUrl + config.apiVersion,
});

const crmInstance = Axios.create({
  baseURL: config.apiUrl,
});

const coreInstance = Axios.create({
  baseURL: config.signalRUrl,
});
const reportInstance = Axios.create({
  baseURL: config.signalRReportUrl,
});

/** Unauthenticated instance – no auth-token check, used for public endpoints (signup, OTP) */
const publicInstance = Axios.create({
  baseURL: config.frontdoorApiUrl,
});

const Instances = {
  instance,
  crmInstance,
  coreInstance,
  reportInstance,
};
export const InstanceKeys = Object.keys(Instances).reduce(
  (prev, curr) => ({ ...prev, [curr]: curr }),
  {}
);

const ApiServices = {
  instance,
  crmInstance,
  /**
   * @name @init
   * @description init axios interceptor on entry point and set/remove auth on app load
   **/
  init() {
    instance.interceptors.request.use((config) => {
      store.dispatch(showLoading(true));
      store.dispatch(startLoading());
      if (
        !checkAuthToken() &&
        !(
          allowedUnAuthUrls(config.data).some((_url) =>
            config.url.includes(_url)
          ) || tokenVerifyUrl(config.url)
        )
      ) {
        toast.error(toastMsg.errors.inValidAuthToken);
        globalLogout(true);
        return false;
      }
      return config;
    });

    instance.interceptors.response.use(
      function (response) {
        // destroy Loader
        destroyLoader();
        // Do something with response data
        return response;
      },
      function (error) {
        if (error?.response?.status === responseStatus.UNAUTHORIZED) {
          storage.deleteItem("Profile");
        }
        // Do something with response error
        destroyLoader();
        return Promise.reject(error);
      }
    );

    // CRM instance for CRM APIs
    crmInstance.interceptors.request.use((config) => {
      store.dispatch(showLoading(true));
      store.dispatch(startLoading());
      if (
        !checkAuthToken() &&
        !(
          allowedUnAuthUrls(config.data).some((_url) =>
            config.url.includes(_url)
          ) || tokenVerifyUrl(config.url)
        )
      ) {
        toast.error(toastMsg.errors.inValidAuthToken);
        globalLogout(true);
        return false;
      }
      return config;
    });

    crmInstance.interceptors.response.use(
      function (response) {
        // destroy Loader
        destroyLoader();
        // Do something with response data
        return response;
      },
      function (error) {
        if (error?.response?.status === responseStatus.UNAUTHORIZED) {
          storage.deleteItem("Profile");
        }
        // Do something with response error
        destroyLoader();
        return Promise.reject(error);
      }
    );

    /* Setting Auth Header*/
  },

  /* General query for get request */
  query(resource, params, instanceKey = InstanceKeys.instance) {
    return Instances[instanceKey].get(resource, params).catch((error) => {
      throw new Error(`ApexChat Generic ApiServices ${error}`);
    });
  },

  /* General get request */
  get(resource, headers, instanceKey = InstanceKeys.instance) {
    this.setAuthHeader(instanceKey);
    return Instances[instanceKey].get(resource, {
      headers,
    });
  },

  /* Download File */
  getFile(resource, headers, instanceKey = InstanceKeys.instance, params = {}) {
    this.setAuthHeader(instanceKey);
    return Instances[instanceKey]
      .get(resource, {
        headers,
        params,
        responseType: "blob",
      })
      .finally(() => {
        // Do not leave instance stuck on blob (breaks later JSON APIs)
        if (Instances[instanceKey]?.defaults) {
          delete Instances[instanceKey].defaults.responseType;
        }
      });
  },

  /* Get request with params*/
  getWithParams(resource, params, instanceKey = InstanceKeys.instance) {
    this.setAuthHeader(instanceKey);
    return Instances[instanceKey].get(resource, params).catch((error) => {
      if (error.message === "Network Error")
        return toast.error(toastMsg.errors.networkError);
      return error?.response?.data?.error;
    });
  },

  /* General post request */
  post(resource, params, instanceKey = InstanceKeys.instance, httpConfig = {}) {
    this.setAuthHeader(instanceKey);
    return Instances[instanceKey].post(resource, params, httpConfig);
  },

  /* Public post – no auth header (signup, OTP, etc.) */
  postPublic(resource, params) {
    return publicInstance.post(resource, params);
  },

  /* Public put request */
  putPublic(resource, params) {
    return publicInstance.put(resource, params);
  },

  /* Public get – no auth header (signup, OTP, etc.) */
  getPublic(resource, params) {
    return publicInstance.get(resource, params);
  },

  /* General post request */
  postAttachements(
    resource,
    params,
    instanceKey = InstanceKeys.instance,
    httpConfig = {}
  ) {
    this.setAuthHeader(instanceKey);
    this.setMultipartHeader(instanceKey);
    return Instances[instanceKey]
      .post(resource, params, httpConfig)
      .catch((error) => {
        if (error.message === "Network Error")
          return toast.error(toastMsg.errors.networkError);
        return error?.response?.data;
      });
  },

  /* General put request */
  put(resource, params, instanceKey = InstanceKeys.instance, httpConfig) {
    this.setAuthHeader(instanceKey);
    this.setMultipartHeader(instanceKey);
    return Instances[instanceKey]
      .put(resource, params, httpConfig)
      .catch((error) => {
        if (error.message === "Network Error")
          return toast.error(toastMsg.errors.networkError);
        return error?.response?.data;
      });
  },

  /* General delete request */
  delete(resource, params, instanceKey = InstanceKeys.instance) {
    return Instances[instanceKey]
      .delete(`${resource}/${params}`)
      .catch((error) => {
        throw new Error(`ApexChat Delete ApiServices ${error}`);
      });
  },
  setAuthHeader(instanceKey = "") {
    const auth = getCookie("CRM-Auth");
    if (auth) {
      Instances[instanceKey].defaults.headers.common["companykey"] =
        getCookie("companyKey");
      Instances[instanceKey].defaults.headers.common["Authorization"] =
        `Bearer ${auth}`;
      Instances[instanceKey].defaults.headers["Content-Type"] =
        `application/json`;
    } else {
      Instances[instanceKey].defaults.headers.common["Authorization"] =
        `Bearer`;
    }
  },

  setMultipartHeader(instanceKey = InstanceKeys.instance) {
    Instances[instanceKey].defaults.headers["Content-Type"] =
      `multipart/form-data`;
  },

  dataWrapper(data, status) {
    // data wrapper will be removed if WebApi project will send data in ideal way. with status code/data/message etc..

    switch (status) {
      case "success":
        return {
          status: data.status || status,
          code: data.code || 200,
          data: data,
          message: data.message || "success",
        };
      case "error":
        return {
          status: data.status || status,
          code: data.code || 400,
          data: data,
          message: data.message || "error",
        };
      default:
        return {
          status: "error",
          code: 400,
          data: null,
          message: "error",
        };
    }
  },
};

// destroy Loader: Complete loading to full and set show loading false
const destroyLoader = () => {
  store.dispatch(endLoading());
  setTimeout(() => {
    store.dispatch(showLoading(false));
  }, 500);
};

export default ApiServices;

export const allowedUnAuthUrls = (data = {}) => [
  "/login",
  "/host/meta",
  RESETPASSWORD(data),
];

const tokenVerifyUrl = (url) => {
  let tempURL = url?.split("?");
  if (tempURL[0] === "/auth/verifyToken") {
    return true;
  } else {
    return false;
  }
};
